/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import {
  deriveEstimateMetrics,
  normalizeEstimatePagination,
  type EstimatesApiMetrics,
} from "@/lib/business-logic/estimates";
import {
  DEFAULT_PAGE_SIZE,
  QUERY_GC_TIME,
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";
import { estimateQueryKeys } from "@/lib/query/query-keys";
import type { Database } from "@/lib/supabase/types";
import { logger } from "@/lib/utils/logger";
import type { PaginationInfo } from "@/lib/utils/responses";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

type EstimateRow = Database["public"]["Tables"]["estimates"]["Row"];
export type Estimate = EstimateRow;

export interface UseEstimatesParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  projectId?: string;
  serviceId?: string;
  clientId?: string;
  divisionId?: string;
  status?: string;
  fields?: "minimal" | "standard" | "full";
  enabled?: boolean;
  staleTime?: number;
}

export interface EstimatesResponse {
  estimates: EstimateRow[];
  pagination: PaginationInfo;
  metrics: ReturnType<typeof deriveEstimateMetrics>;
}

const DEFAULT_METRICS: EstimatesResponse["metrics"] = {
  total: 0,
  active: 0,
  draft: 0,
  completed: 0,
  totalValue: 0,
  winRate: 0,
  wonValue: 0,
  avgEstimateValue: 0,
  statusCounts: {},
};

const EMPTY_ESTIMATES: EstimateRow[] = [];

export function useEstimates(params: UseEstimatesParams = {}) {
  const { getToken } = useAuthSafe();
  const queryClient = useQueryClient();

  const {
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    sortBy = "created_at",
    sortOrder = "desc",
    search,
    projectId,
    serviceId,
    clientId,
    divisionId,
    status,
    fields = "standard",
    enabled: rawEnabled = true,
    staleTime: _staleTime = 2 * 60 * 1000, // Not used, kept for API compatibility
  } = params;

  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled: rawEnabled,
    debugKey: "useEstimates",
  });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  // Build filters object for query key
  const filters = useMemo(
    () => ({
      search: search ?? undefined,
      projectId: projectId ?? undefined,
      serviceId: serviceId ?? undefined,
      clientId: clientId ?? undefined,
      divisionId: divisionId ?? undefined,
      status: status ?? undefined,
      fields: fields ?? undefined,
    }),
    [search, projectId, serviceId, clientId, divisionId, status, fields],
  );

  // Use centralized query key factory
  const queryKey = useMemo(
    () =>
      estimateQueryKeys.list(
        organizationId ?? undefined,
        filters,
        { field: sortBy, direction: sortOrder },
        page,
        limit,
      ),
    [organizationId, filters, sortBy, sortOrder, page, limit],
  );

  const { data, isLoading, error, isError, refetch, isFetching } =
    useQuery<EstimatesResponse>({
      queryKey,
      queryFn: async (): Promise<EstimatesResponse> => {
        if (!organizationId) {
          throw new Error("Organization ID is required");
        }

        const searchParams = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          sortBy,
          sortOrder,
          ...(search && { query: search }),
          ...(projectId && { projectId }),
          ...(serviceId && { serviceId }),
          ...(clientId && { clientId }),
          ...(divisionId && { divisionId }),
          ...(status && { status }),
          ...(fields && { fields }),
        });

        const result = await api.get<EstimateRow[]>(
          `/api/estimates?${searchParams.toString()}`,
        );

        if (isApiFailure(result)) {
          logger.error("Failed to fetch estimates:", result);
          throw new Error(result.message || "Failed to load estimates");
        }

        const estimateRows = Array.isArray(result.data) ? result.data : [];
        const pagination = normalizeEstimatePagination(
          result.pagination,
          page,
          limit,
          estimateRows.length,
        );
        const metrics = deriveEstimateMetrics(undefined, pagination);

        return {
          estimates: estimateRows,
          pagination,
          metrics,
        };
      },
      enabled: queryEnabled,
      staleTime: QUERY_STALE_TIME.CRITICAL, // estimates are CRITICAL
      gcTime: QUERY_GC_TIME.CRITICAL,
      retry: QUERY_RETRY.AGGRESSIVE,
      retryDelay: getRetryDelay,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: estimateQueryKeys.lists(),
      refetchType: "active", // Only refetch active queries
    });
  }, [queryClient]);

  const hasMore = data?.pagination?.hasMore ?? false;

  const loadMore = useCallback(() => {
    if (hasMore) {
      return refetch();
    }
    return undefined;
  }, [hasMore, refetch]);

  return {
    estimates: data?.estimates ?? EMPTY_ESTIMATES,
    pagination:
      data?.pagination ??
      normalizeEstimatePagination(
        undefined,
        page,
        limit,
        data?.estimates?.length,
      ),
    metrics: data?.metrics ?? { ...DEFAULT_METRICS },
    isLoading,
    isFetching,
    error: isError
      ? error instanceof Error
        ? error.message
        : "Failed to load estimates"
      : null,
    refetch,
    invalidate,
    loadMore,
  };
}

export function useEstimatesSimple(params?: UseEstimatesParams) {
  return useEstimates(params);
}

export function useEstimatesOptimized() {
  return useEstimates();
}

export function useEstimatesByClient(options: {
  clientId?: string;
  enabled?: boolean;
}) {
  return useEstimates({
    clientId: options.clientId,
    enabled: options.enabled,
  });
}

export function useEstimatesByProject(options: {
  projectId?: string;
  enabled?: boolean;
}) {
  return useEstimates({
    projectId: options.projectId,
    enabled: options.enabled,
  });
}

export function useEstimatesMetrics(
  params: {
    status?: string;
    projectId?: string;
    serviceId?: string;
    clientId?: string;
    search?: string;
  } = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled,
    debugKey: "useEstimatesMetrics",
  });

  const api = createApiClient({
    getToken,
    getOrganizationId: () => organizationId ?? undefined,
    autoRetry429: true,
  });

  const normalizedFilters = useMemo(() => {
    const trimmedSearch = params.search?.trim();
    return {
      status:
        params.status && params.status !== "all" ? params.status : undefined,
      projectId: params.projectId,
      serviceId: params.serviceId,
      clientId: params.clientId,
      search:
        trimmedSearch && trimmedSearch.length > 0 ? trimmedSearch : undefined,
    };
  }, [
    params.status,
    params.projectId,
    params.serviceId,
    params.clientId,
    params.search,
  ]);

  const queryKey = useMemo(
    () =>
      estimateQueryKeys.metrics(organizationId ?? undefined, normalizedFilters),
    [organizationId, normalizedFilters],
  );

  return useQuery<EstimatesApiMetrics>({
    queryKey,
    enabled: queryEnabled && enabled,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    retry: QUERY_RETRY.AGGRESSIVE,
    retryDelay: getRetryDelay,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<EstimatesApiMetrics> => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      const searchParams = new URLSearchParams();
      if (normalizedFilters.status) {
        searchParams.set("status", normalizedFilters.status);
      }
      if (normalizedFilters.projectId) {
        searchParams.set("projectId", normalizedFilters.projectId);
      }
      if (normalizedFilters.serviceId) {
        searchParams.set("serviceId", normalizedFilters.serviceId);
      }
      if (normalizedFilters.clientId) {
        searchParams.set("clientId", normalizedFilters.clientId);
      }
      if (normalizedFilters.search) {
        searchParams.set("query", normalizedFilters.search);
      }

      const url = `/api/estimates/metrics${
        searchParams.toString() ? `?${searchParams.toString()}` : ""
      }`;

      const result = await api.get<EstimatesApiMetrics>(url);
      if (isApiFailure(result)) {
        logger.error("Failed to fetch estimate metrics", { error: result });
        throw new Error(result.message || "Failed to fetch estimate metrics");
      }

      return (
        result.data ?? {
          statusCounts: {},
          totalValue: 0,
          wonValue: 0,
          winRate: 0,
          avgEstimateValue: 0,
        }
      );
    },
  });
}
